import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trophy, Users, Plus, X, Send, Star, Building2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORTS = ["tennis", "pickleball", "golf", "table-tennis", "ski", "padel", "squash", "badminton", "climbing"];
const SPORT_LABELS = {
  "tennis": "Tennis", "pickleball": "Pickleball", "golf": "Golf", "table-tennis": "Table Tennis",
  "ski": "Skiing", "padel": "Padel", "squash": "Squash", "badminton": "Badminton", "climbing": "Climbing"
};

function LeagueCard({ league, userId, onJoin, onSelect }) {
  const isMember = league.member_ids?.includes(userId);
  const isOrganizer = league.organizer_id === userId;

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-5 hover:border-brand-orange/30 transition-all cursor-pointer group"
      onClick={() => onSelect(league)}
      data-testid="league-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-bold text-brand-navy group-hover:text-brand-orange transition-colors">{league.name}</h3>
          <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500">
            <Building2 size={11} />
            <span>{league.company_name}</span>
          </div>
        </div>
        <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-brand-orange/10 text-brand-orange rounded capitalize">
          {SPORT_LABELS[league.sport] || league.sport}
        </span>
      </div>
      {league.description && (
        <p className="text-xs text-gray-500 mb-3">{league.description}</p>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <Users size={11} />
          <span>{league.member_ids?.length || 0} members</span>
          <span className="mx-1">·</span>
          <Trophy size={11} />
          <span>{league.results?.length || 0} matches</span>
        </div>
        {!isMember && !isOrganizer && (
          <button
            onClick={(e) => { e.stopPropagation(); onJoin(league.id); }}
            className="text-xs text-brand-orange border border-brand-orange/30 px-2 py-1 rounded hover:bg-brand-orange/10 transition-colors"
            data-testid="join-league-btn"
          >
            Join
          </button>
        )}
        {(isMember || isOrganizer) && (
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Star size={10} className="text-brand-orange fill-brand-orange" />
            {isOrganizer ? "Organizer" : "Member"}
          </span>
        )}
      </div>
    </div>
  );
}

function LeagueDetail({ league, userId, onClose, onPostResult, onInvite, leaderboard }) {
  const { t } = useTranslation();
  const [inviteEmail, setInviteEmail] = useState("");
  const [resultForm, setResultForm] = useState({ winner_id: userId, loser_id: "", score: "" });
  const [tab, setTab] = useState("leaderboard");
  const isMember = league.member_ids?.includes(userId);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="league-detail-modal">
      <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-heading font-bold text-brand-navy">{league.name}</h2>
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                <Building2 size={13} />
                <span>{league.company_name}</span>
                <span>·</span>
                <span className="capitalize">{SPORT_LABELS[league.sport] || league.sport}</span>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-600 hover:text-brand-navy transition-colors p-1">
              <X size={20} />
            </button>
          </div>

          {league.description && (
            <p className="text-sm text-gray-500 mb-5">{league.description}</p>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-5 border-b border-gray-200 pb-3">
            {["leaderboard", "results", "invite"].map((t_) => (
              (t_ !== "invite" || league.organizer_id === userId) && (
                <button
                  key={t_}
                  onClick={() => setTab(t_)}
                  className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors capitalize ${tab === t_ ? "bg-brand-orange text-white" : "text-gray-500 hover:text-brand-navy"}`}
                >
                  {t_}
                </button>
              )
            ))}
            {isMember && (
              <button
                onClick={() => setTab("post-result")}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${tab === "post-result" ? "bg-brand-orange text-white" : "text-gray-500 hover:text-brand-navy"}`}
              >
                Post Result
              </button>
            )}
          </div>

          {/* Leaderboard */}
          {tab === "leaderboard" && (
            <div className="space-y-2" data-testid="leaderboard-section">
              {leaderboard.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No matches played yet. Post the first result!</p>
              ) : (
                leaderboard.map((entry, i) => (
                  <div key={entry.user_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-lg font-black text-slate-600 w-6 text-center">#{i + 1}</span>
                    {entry.avatar_url ? (
                      <img src={entry.avatar_url} alt={entry.name} className="w-8 h-8 rounded-lg object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-brand-orange/20 flex items-center justify-center text-brand-orange font-bold text-xs">
                        {entry.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1">
                      <span className="font-medium text-brand-navy text-sm">{entry.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-gray-500">{entry.wins}W / {entry.losses}L</span>
                      <span className="font-bold text-brand-orange">{entry.points} pts</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Results history */}
          {tab === "results" && (
            <div className="space-y-2">
              {league.results?.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No results yet.</p>
              ) : (
                league.results?.map((r) => (
                  <div key={r.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm">
                    <span className="text-slate-300">{r.score || "Match played"}</span>
                    <span className="text-xs text-slate-500">{r.posted_at?.slice(0, 10)}</span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Invite */}
          {tab === "invite" && league.organizer_id === userId && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Invite team members by email. They'll be able to join this league.</p>
              <div className="flex gap-2">
                <input
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-brand-navy text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
                  data-testid="invite-email-input"
                />
                <button
                  onClick={() => { onInvite(league.id, inviteEmail); setInviteEmail(""); }}
                  className="flex items-center gap-1.5 px-4 py-2 bg-brand-orange text-white font-bold text-sm rounded-lg hover:bg-brand-orange-hover transition-colors"
                  data-testid="invite-send-btn"
                >
                  <Send size={14} /> Send
                </button>
              </div>
              {league.invite_emails?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Invited:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {league.invite_emails.map(em => (
                      <span key={em} className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{em}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Post Result */}
          {tab === "post-result" && isMember && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Record the outcome of a match between league members.</p>
              <div>
                <label htmlFor="Opponent-User-ID" className="block text-xs text-gray-500 mb-1">Opponent (User ID)</label><input id="Opponent-User-ID"
                  value={resultForm.loser_id}
                  onChange={(e) => setResultForm(p => ({ ...p, loser_id: e.target.value }))}
                  placeholder="Opponent's user ID"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-brand-navy text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
                  data-testid="result-loser-input"
                />
              </div>
              <div>
                <label htmlFor="Score-E-G-6" className="block text-xs text-gray-500 mb-1">Score (e.g. 6-3, 6-4)</label><input id="Score-E-G-6"
                  value={resultForm.score}
                  onChange={(e) => setResultForm(p => ({ ...p, score: e.target.value }))}
                  placeholder="6-3, 6-4"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-brand-navy text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
                  data-testid="result-score-input"
                />
              </div>
              <button
                onClick={() => onPostResult(league.id, resultForm)}
                disabled={!resultForm.loser_id || !resultForm.score}
                className="w-full py-2.5 bg-brand-orange text-white font-bold rounded-lg hover:bg-brand-orange-hover transition-colors disabled:opacity-50"
                data-testid="post-result-btn"
              >
                Submit Result
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LeaguesPage() {
  const { user } = useAuth();
  const showToast = useToast();
  const { t } = useTranslation();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [form, setForm] = useState({ name: "", sport: "tennis", company_name: "", description: "", is_public: true });
  const [creating, setCreating] = useState(false);

  const fetchLeagues = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leagues`, { withCredentials: true });
      setLeagues(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeagues(); }, []);

  const fetchLeaderboard = async (leagueId) => {
    try {
      const { data } = await axios.get(`${API}/leagues/${leagueId}/leaderboard`, { withCredentials: true });
      setLeaderboard(data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSelect = async (league) => {
    setSelectedLeague(league);
    await fetchLeaderboard(league.id);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/leagues`, form, { withCredentials: true });
      setLeagues(prev => [data, ...prev]);
      setShowCreate(false);
      setForm({ name: "", sport: "tennis", company_name: "", description: "", is_public: true });
      showToast("League created!");
    } catch (e) {
      showToast("Failed to create league", "error");
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async (leagueId) => {
    try {
      await axios.post(`${API}/leagues/${leagueId}/join`, {}, { withCredentials: true });
      showToast("Joined league!");
      fetchLeagues();
    } catch (e) {
      showToast(e.response?.data?.detail || "Could not join league", "error");
    }
  };

  const handleInvite = async (leagueId, email) => {
    if (!email) return;
    try {
      const { data } = await axios.post(`${API}/leagues/${leagueId}/invite`, { email }, { withCredentials: true });
      setSelectedLeague(data);
      showToast(`Invited ${email}!`);
    } catch (e) {
      showToast("Failed to send invite", "error");
    }
  };

  const handlePostResult = async (leagueId, resultData) => {
    try {
      const { data } = await axios.post(`${API}/leagues/${leagueId}/results`, resultData, { withCredentials: true });
      setSelectedLeague(data);
      await fetchLeaderboard(leagueId);
      showToast("Result posted!");
    } catch (e) {
      showToast("Failed to post result", "error");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <Trophy size={48} className="mx-auto text-brand-orange mb-4" />
          <h1 className="text-2xl font-heading font-bold text-brand-navy mb-2">Corporate Wellness Leagues</h1>
          <p className="text-gray-500 mb-6">Sign in to create or join a league.</p>
          <Link to="/login" className="px-6 py-3 bg-brand-orange text-white font-bold rounded-lg hover:bg-brand-orange-hover transition-colors">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 py-8 px-4">
        <div className="max-w-6xl mx-auto flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-black text-brand-navy mb-1 flex items-center gap-2">
              <Trophy size={28} className="text-brand-orange" /> {t("leagues.title")}
            </h1>
            <p className="text-gray-500 text-sm">{t("leagues.subtitle")}</p>
          </div>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-orange text-white font-bold rounded-lg hover:bg-brand-orange-hover transition-colors whitespace-nowrap"
            data-testid="create-league-btn"
          >
            <Plus size={16} /> {t("leagues.createLeague")}
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Create form */}
        {showCreate && (
          <form onSubmit={handleCreate} className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm" data-testid="create-league-form">
            <h2 className="font-bold text-brand-navy mb-4">{t("leagues.createLeague")}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="LeaguesleagueName" className="block text-xs text-gray-500 mb-1">{t("leagues.leagueName")} *</label><input id="LeaguesleagueName"
                  required
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Q1 Tennis League"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-brand-navy text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
                  data-testid="league-name-input"
                />
              </div>
              <div>
                <label htmlFor="LeaguescompanyName" className="block text-xs text-gray-500 mb-1">{t("leagues.companyName")} *</label><input id="LeaguescompanyName"
                  required
                  value={form.company_name}
                  onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))}
                  placeholder="Acme Corp"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-brand-navy text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
                  data-testid="league-company-input"
                />
              </div>
              <div>
                <label htmlFor="Leaguessport" className="block text-xs text-gray-500 mb-1">{t("leagues.sport")} *</label><select id="Leaguessport"
                  value={form.sport}
                  onChange={e => setForm(p => ({ ...p, sport: e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-brand-navy text-sm focus:outline-none"
                  data-testid="league-sport-select"
                >
                  {SPORTS.map(s => <option key={s} value={s}>{SPORT_LABELS[s]}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="Leaguesdescription" className="block text-xs text-gray-500 mb-1">{t("leagues.description")}</label><input id="Leaguesdescription"
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Weekly lunch-break matches..."
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-brand-navy text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
                  data-testid="league-description-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <input
                type="checkbox"
                id="is_public"
                checked={form.is_public}
                onChange={e => setForm(p => ({ ...p, is_public: e.target.checked }))}
                className="w-4 h-4"
              />
              <label htmlFor="is_public" className="text-sm text-slate-300">Allow anyone to join (public league)</label>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                type="submit"
                disabled={creating}
                className="px-6 py-2.5 bg-brand-orange text-white font-bold rounded-lg hover:bg-brand-orange-hover transition-colors disabled:opacity-50"
                data-testid="create-league-submit"
              >
                {creating ? "Creating..." : t("leagues.createLeague")}
              </button>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-4 py-2.5 border border-gray-200 text-gray-500 rounded-xl hover:text-brand-navy transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Leagues grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-brand-orange border-t-transparent rounded-full animate-spin" />
          </div>
        ) : leagues.length === 0 ? (
          <div className="text-center py-20">
            <Trophy size={48} className="mx-auto text-slate-600 mb-4" />
            <h3 className="text-lg font-bold text-brand-navy mb-2">No Leagues Yet</h3>
            <p className="text-gray-500 text-sm mb-6">Be the first to create a corporate wellness league!</p>
            <button
              onClick={() => setShowCreate(true)}
              className="px-6 py-3 bg-brand-orange text-white font-bold rounded-lg hover:bg-brand-orange-hover transition-colors"
            >
              Create First League
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="leagues-grid">
            {leagues.map(league => (
              <LeagueCard
                key={league.id}
                league={league}
                userId={user?.id}
                onJoin={handleJoin}
                onSelect={handleSelect}
              />
            ))}
          </div>
        )}
      </div>

      {/* League detail modal */}
      {selectedLeague && (
        <LeagueDetail
          league={selectedLeague}
          userId={user?.id}
          onClose={() => setSelectedLeague(null)}
          onPostResult={handlePostResult}
          onInvite={handleInvite}
          leaderboard={leaderboard}
        />
      )}
    </div>
  );
}
